import { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AriaMark } from "@/components/AriaMark";
import { Loader2, UploadCloud, X, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export const AddReceiptDialog = ({ open, onOpenChange, onCreated }) => {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);

  const onDrop = useCallback((files) => {
    const f = files[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("File too large — 8MB max"); return; }
    const reader = new FileReader();
    reader.onload = () => setFile({ name: f.name, dataUrl: reader.result });
    reader.readAsDataURL(f);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { "image/*": [] }, multiple: false,
  });

  const process = async () => {
    if (!file || processing) return;
    setProcessing(true);
    try {
      const r = await api.post("/aria/receipt", { image_base64: file.dataUrl, filename: file.name }, { timeout: 120000 });
      const p = r.data.parsed || {};
      if (r.data.violation) toast.warning(`Filed & flagged — ${r.data.violation}`);
      else if (r.data.auto_approved) toast.success(`${p.merchant || "Receipt"} filed & auto-approved — $${(p.total || 0).toLocaleString()}`);
      else toast.success(`${p.merchant || "Receipt"} filed — routed to review`);
      setFile(null);
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aria could not read this receipt");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!processing) { onOpenChange(o); if (!o) setFile(null); } }}>
      <DialogContent className="max-w-md border-[#222] bg-[#141414] text-white" data-testid="add-receipt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]"><AriaMark size={20} /> Add receipt</DialogTitle>
          <DialogDescription className="text-[12px] text-[#A3A3A3]">
            Drop a receipt image — Aria reads it, checks policy, assigns the GL and files the expense.
          </DialogDescription>
        </DialogHeader>

        {!file ? (
          <div {...getRootProps()} data-testid="add-receipt-dropzone"
            className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed px-6 py-12 text-center transition-colors ${
              isDragActive ? "border-[rgba(79,142,247,0.6)] bg-[rgba(79,142,247,0.06)]" : "border-[#2e2e2e] bg-[#111] hover:border-[#3a3a3a]"}`}>
            <input {...getInputProps()} data-testid="add-receipt-input" />
            <UploadCloud size={26} className="text-[#4F8EF7]" />
            <div className="mt-2.5 text-[13px] font-medium">{isDragActive ? "Drop it — Aria's got it" : "Drop a receipt or click to browse"}</div>
            <div className="mt-1 text-[11px] text-[#6b6b6b]">PNG, JPG or WEBP · up to 8MB</div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative overflow-hidden rounded-xl border border-[#242424] bg-[#0d0d0d] p-2">
              <img src={file.dataUrl} alt="receipt preview" className="max-h-56 w-full rounded-lg object-contain" data-testid="add-receipt-preview" />
              {!processing && (
                <button onClick={() => setFile(null)} data-testid="add-receipt-remove"
                  className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full border border-[#2a2a2a] bg-[rgba(10,10,10,0.85)] text-[#A3A3A3] hover:text-white">
                  <X size={13} />
                </button>
              )}
            </div>
            {processing && (
              <div className="flex items-center gap-2.5 rounded-xl border border-[rgba(79,142,247,0.2)] bg-[rgba(79,142,247,0.05)] px-4 py-3">
                <Loader2 size={13} className="animate-spin text-[#4F8EF7]" />
                <span className="text-[12.5px] text-[#7FAEF9]">Aria is reading the receipt and running policy checks…</span>
              </div>
            )}
            <Button onClick={process} disabled={processing} data-testid="add-receipt-process"
              className="h-9 w-full btn-gradient text-[13px] font-medium text-white disabled:opacity-40">
              {processing ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Sparkles size={14} className="mr-1.5" />}
              {processing ? "Processing…" : "Let Aria file it"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
